import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, Loader2, Home, Mail } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [subscriptionData, setSubscriptionData] = useState(null);

  const subscriptionId = searchParams.get('subscription_id');
  const paymentId = searchParams.get('paymentId') || searchParams.get('Id');

  const t = {
    en: {
      verifying: 'Verifying your payment...',
      success: 'Payment Successful!',
      welcome: 'Welcome to NuzhaTrips Premium',
      thankYou: 'Thank you for your subscription. You now have access to all exclusive flight deals!',
      whatNext: 'What happens next?',
      point1: 'You will receive exclusive flight deals weekly via email and WhatsApp',
      point2: 'You get access to 100% of all deals',
      point3: 'Priority WhatsApp support',
      emailSent: 'A confirmation email has been sent to your inbox',
      goHome: 'Go to Homepage',
      subscriptionId: 'Subscription ID',
      plan: 'Plan',
      amount: 'Amount Paid'
    },
    ar: {
      verifying: 'جاري التحقق من الدفع...',
      success: 'تم الدفع بنجاح!',
      welcome: 'مرحباً بك في NuzhaTrips Premium',
      thankYou: 'شكراً لاشتراكك. لديك الآن حق الوصول إلى جميع عروض الطيران الحصرية!',
      whatNext: 'ماذا يحدث الآن؟',
      point1: 'ستتلقى عروض طيران حصرية أسبوعياً عبر البريد الإلكتروني والواتساب',
      point2: 'ستحصل على 100٪ من جميع العروض',
      point3: 'دعم أولوية عبر واتساب',
      emailSent: 'تم إرسال بريد تأكيد إلى بريدك الإلكتروني',
      goHome: 'العودة للصفحة الرئيسية',
      subscriptionId: 'رقم الاشتراك',
      plan: 'الخطة',
      amount: 'المبلغ المدفوع'
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      if (!subscriptionId && !paymentId) {
        setVerificationStatus('success');
        return;
      }

      try {
        // First try to verify via callback if paymentId exists
        if (paymentId) {
          const callbackResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/payment/callback?paymentId=${paymentId}&subscription_id=${subscriptionId || ''}`
          );
          const callbackData = await callbackResponse.json();
          
          if (callbackData.success && callbackData.status === 'completed') {
            setVerificationStatus('success');
            setSubscriptionData(callbackData);
            return;
          }
        }

        // Otherwise verify via subscription ID
        if (subscriptionId) {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify/${subscriptionId}`
          );
          const data = await response.json();

          if (data.success && data.status === 'active') {
            setVerificationStatus('success');
            setSubscriptionData(data.subscription);
          } else {
            setVerificationStatus('success'); // Show success page anyway
          }
        }
      } catch (error) {
        console.error('Verification error:', error);
        setVerificationStatus('success'); // Show success page even if verification fails
      }
    };

    verifyPayment();
  }, [subscriptionId, paymentId]);

  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        {verificationStatus === 'verifying' ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-16 h-16 mx-auto text-orange-500 animate-spin mb-4" />
            <p className="text-xl text-gray-600">{t[language].verifying}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center text-white">
              <CheckCircle className="w-20 h-20 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-2">{t[language].success}</h1>
              <p className="text-xl opacity-90">{t[language].welcome}</p>
            </div>

            {/* Content */}
            <div className="p-8">
              <p className="text-lg text-gray-600 mb-6 text-center">
                {t[language].thankYou}
              </p>

              {/* Subscription Details */}
              {subscriptionData && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {subscriptionData.id && (
                      <>
                        <span className="text-gray-500">{t[language].subscriptionId}:</span>
                        <span className="font-medium">{subscriptionData.id.substring(0, 8)}...</span>
                      </>
                    )}
                    <span className="text-gray-500">{t[language].plan}:</span>
                    <span className="font-medium">Premium (Yearly)</span>
                    <span className="text-gray-500">{t[language].amount}:</span>
                    <span className="font-medium">35 OMR</span>
                  </div>
                </div>
              )}

              {/* What happens next */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">{t[language].whatNext}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{t[language].point1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{t[language].point2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{t[language].point3}</span>
                  </li>
                </ul>
              </div>

              {/* Email notification */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6 flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-500" />
                <span className="text-blue-700">{t[language].emailSent}</span>
              </div>

              {/* Go Home Button */}
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:from-orange-600 hover:to-pink-600 transition-all"
              >
                <Home className="w-5 h-5" />
                {t[language].goHome}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
